// frontend/src/lib/pwa.js
//
// Bootstrap PWA côté client : enregistrement du service worker généré
// par vite-plugin-pwa (virtual:pwa-register/react).
//
// On a choisi registerType: 'autoUpdate' côté vite.config — donc le
// nouveau SW prend immédiatement au prochain navigate. Pas de prompt
// utilisateur dans cette v1 ; si on veut un toast "nouvelle version
// disponible" plus tard, basculer en 'prompt' et utiliser `needRefresh`.
//
// On expose un hook `useOfflineBanner` qui retourne true quand le browser
// est offline (utilisé par PrivateLayout pour afficher une bannière).

import { useEffect, useState } from 'react';

// Le module 'virtual:pwa-register' est virtuel : injecté par vite-plugin-pwa
// au build. En dev sans le plugin actif ou en jsdom (tests), il échoue. On
// wrappe dans un try/catch pour rester silencieux dans ces cas.
export async function registerServiceWorker() {
  if (typeof window === 'undefined') return; // SSR safety
  if (!('serviceWorker' in navigator)) return; // navigateur trop vieux
  try {
    // Import dynamique : si le virtual module n'existe pas (jsdom tests,
    // dev sans plugin), on l'attrape silencieusement.
    const mod = await import('virtual:pwa-register');
    const updateSW = mod.registerSW({
      immediate: true,
      onRegistered(reg) {
        // Vérifie une mise à jour toutes les heures côté client — couvre les
        // sessions PWA long-lived (utilisateur garde l'app ouverte plusieurs
        // jours sans quitter, ce qui est courant en mode "installé").
        if (reg) {
          setInterval(
            () => {
              reg.update().catch(() => {});
            },
            60 * 60 * 1000
          );
        }
      },
      onRegisterError(err) {
        // En prod c'est un signal utile ; en dev local sans HTTPS le SW ne
        // s'enregistre simplement pas (browser policy), donc on ne crie pas.
        if (import.meta.env.PROD) {
          console.warn('[pwa] SW register error:', err);
        }
      },
    });
    return updateSW;
  } catch {
    // Module virtuel absent (cas test/dev sans plugin) — no-op.
    return undefined;
  }
}

// ── Hook : détecte l'état online/offline du browser ──────────────────────────
//
// Sert au composant <OfflineBanner /> qui rend une bandeau visible quand on
// est offline. navigator.onLine n'est pas 100% fiable (un Wi-Fi connecté
// sans gateway renvoie quand même true) mais c'est l'API standard et ça
// couvre 95% des cas pratiques.
export function useOfflineBanner() {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return isOffline;
}
